/*
* Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
* SPDX-License-Identifier: MIT
*
* Licensed under the MIT License. See the LICENSE accompanying this file
* for the specific language governing permissions and limitations under
* the License.
*/

import React from 'react';
import { AmplifySignIn } from '@aws-amplify/ui-react';
import { I18n } from '@aws-amplify/core';

import Button from '@material-ui/core/Button';

import Config from 'Config';
import { getCurrentBrand } from '../../utils/applyBrand';

// 「お客様情報登録」遷移先（brand × 環境）
const REGISTER_LINKS = {
	halmekUp: {
		prod:    'https://halmek.co.jp/membership/free/contact?free_registration=contact',
		nonProd: 'https://dev.www.dev.halmek.co.jp/membership/free/contact?free_registration=contact',
	},
	halmekEvents: {
		prod:    'https://events.halmek.co.jp/signup',
		nonProd: 'https://stg.events.halmek.co.jp/signup',
	},
};

// 「お問い合わせ」遷移先（環境差なし、brand のみで決定）
const CONTACT_LINKS = {
	halmekUp:     'https://info.halmek.co.jp/inquiry/index.php?site_id=01',
	halmekEvents: 'https://events.halmek.co.jp/entry_guide',
};

function openExternal(url) {
	if (!url) return;
	window.open(url, '_blank', 'noopener,noreferrer');
}

const Login = () => {
	// primary-footer-content slot に置いた <button type="submit"> は Web Components の
	// 構造上、shadow DOM 内の amplify form を見つけられず submit イベントを発火できない。
	// （slotted element は projection 先ではなく light DOM ツリーに属するため）
	// shadow DOM 内の form を直接取得して requestSubmit で発火させる。
	const handleSignInClick = () => {
		const signInEl = document.querySelector('amplify-sign-in');
		const form = signInEl?.shadowRoot?.querySelector('form');
		if (form?.requestSubmit) {
			form.requestSubmit();
		} else {
			form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
		}
	};

	const handleRegisterClick = () => {
		const brand = getCurrentBrand();
		const envKey = Config.isProd ? 'prod' : 'nonProd';
		openExternal(REGISTER_LINKS[brand]?.[envKey]);
	};
	const handleContactClick = () => {
		const brand = getCurrentBrand();
		openExternal(CONTACT_LINKS[brand]);
	};

	return (
		<AmplifySignIn className="login"
			slot="sign-in"
			hideSignUp={true}
			formFields={[
				{
					type: "username",
					label: I18n.get("SIGNIN_USERNAME_LABEL"),
					placeholder: I18n.get("SIGNIN_USERNAME_PLACEHOLDER"),
					required: true,
					inputProps: {
						'data-test': 'sign-in-username-input',
					},
				},
				{
					type: "password",
					label: I18n.get("SIGNIN_PASSWORD_LABEL"),
					placeholder: I18n.get("SIGNIN_PASSWORD_PLACEHOLDER"),
					required: true,
					inputProps: {
						'data-test': 'sign-in-password-input',
					},
				}
			]}>
			<div slot="federated-buttons"></div>
			{/*
			  * primary-footer-content slot を上書きして、ログイン送信ボタン + 「お客様情報登録」+ 「お問い合わせ」+ プライバシーマーク
			  * を halmek デザイン通り単一の .login-footer-actions コンテナに集約。
			  * これにより 4 要素全てが同じ flex container（gap: 12px）で並び、間隔が確実に統一される。
			  */}
			<div slot="primary-footer-content" className="login-footer-actions">
				<Button
					type="button"
					variant="contained"
					color="primary"
					data-test="sign-in-sign-in-button"
					onClick={handleSignInClick}
				>
					{I18n.get('LOGIN_FOOTER_SIGNIN_LABEL')}
				</Button>
				<Button
					type="button"
					variant="contained"
					color="primary"
					onClick={handleRegisterClick}
				>
					{I18n.get('LOGIN_FOOTER_REGISTER_LABEL')}
				</Button>
				<Button
					type="button"
					variant="outlined"
					color="primary"
					className="login-footer--contact"
					onClick={handleContactClick}
				>
					{I18n.get('LOGIN_FOOTER_CONTACT_LABEL')}
				</Button>
				<div className="login-footer-pmark">
					<img src="/assets/img/ico-pmark.png" alt="プライバシーマーク" />
				</div>
			</div>
		</AmplifySignIn>
	);
}

export default Login;
